<script setup>
import { WdsButtonLink } from '@wds/components';

const props = defineProps({
  tag: {
    type: String,
    required: false,
    default: 'div',
  },
  locationName: {
    type: String,
    required: false,
  },
  title: {
    type: String,
    required: false,
  },
  otherLocations: {
    type: Array,
    required: false,
    default: () => [],
  },
  maxVisibleLocations: {
    type: Number || null,
    required: false,
    default: null,
  },
  propertyTypeObj: {
    type: Object,
    required: true,
  },
});
const { t } = useI18n();
const { $mq } = useNuxtApp();
const MAX_MOBILE_NUM_PILLS = 8;
const MAX_DESKTOP_NUM_PILLS = 15;

const expanded = ref(false);

const maxNumPills = computed(() => {
  return props?.maxVisibleLocations || ($mq.isLargeScreen?.value ? MAX_DESKTOP_NUM_PILLS : MAX_MOBILE_NUM_PILLS);
});

const otherLocationsTitle = computed(() => {
  return props?.title || t('t_OTHERLOCATIONSINCOUNTRY', { COUNTRYNAME: `<span>${props?.locationName}</span>` });
});

function toggleExpanded() {
  expanded.value = !expanded?.value;
}

function viewAllPropertiesInLocationText(location) {
  return t('t_VIEWALLPROPERTIESINLOCATION', {
    PROPERTYTYPE: t(props?.propertyTypeObj?.transcode).toLowerCase(),
    LOCATION: location?.name,
  });
}
</script>

<template>
  <component
    :is="tag"
    v-if="otherLocations.length"
    class="other-locations-section"
  >
    <a id="otherlocations" />
    <h2
      class="other-locations-heading"
      v-html="otherLocationsTitle"
    />
    <div
      class="other-locations-catalog"
      :class="{ expanded }"
    >
      <div
        v-for="(location, idx) in otherLocations"
        :key="idx"
        class="other-location-box"
        :class="{ 'extra-location': idx >= maxNumPills }"
      >
        <CommonHwPill
          :pill-link="location.url"
          :pill-text="location.name"
          :aria-label="viewAllPropertiesInLocationText(location)"
          :title="viewAllPropertiesInLocationText(location)"
          rel="noopener"
        />
      </div>
    </div>
    <WdsButtonLink
      v-if="otherLocations.length > maxNumPills"
      target="_blank"
      rel="noopener"
      class="other-locations-show-more"
      :text="$t(expanded ? 't_SHOWLESS' : 't_SHOWMORE')"
      :icon-end="expanded ? 'chevron-up' : 'chevron-down'"
      @click="toggleExpanded()"
    />
  </component>
</template>

<style lang="scss" scoped>
.other-locations-section {
  display: flex;
  flex-direction: column;
  gap: $wds-spacing-m;
  position: relative;

  #otherlocations {
    position: absolute;
    top: wds-rem(-200px);
  }

  .other-locations-heading {
    @include title-2-bld;

    margin-bottom: $wds-spacing-s;
    color: $wds-color-ink-darker;

    :deep(span) {
      color: $wds-color-purple;
    }
  }

  .other-locations-catalog {
    display: flex;
    flex-wrap: wrap;
    gap: $wds-spacing-m;

    .other-location-box {
      &.extra-location {
        display: none;
      }

      &:last-child {
        margin-right: 0;
      }
    }

    &.expanded .other-location-box.extra-location {
      display: block;
    }
  }

  .other-locations-show-more {
    text-transform: capitalize;
    align-self: flex-end;
  }

  @include desktop {
    .other-locations-heading {
      @include title-1-bld;
    }
  }
}
</style>
